import { Injectable } from '@angular/core';
import { TrainingCourseForUser } from 'app/_models/training/training-course-for-user.model';
import { TrainingCourseQuestionSectionResponse } from 'app/_models/training/training-course-question-section-response.model';
import { TrainingCourseSection } from 'app/_models/training/training-course-section.model';
import { TrainingSectionType } from 'app/_models/training/training-section-type';
import { forkJoin, of } from 'rxjs';
import { Observable } from 'rxjs/Observable';
import { catchError, concatMap } from 'rxjs/operators';
import { TrainingService } from './training.service';

@Injectable()
export class TrainingEnergiaOverrideService {
  private readonly ENERGIA_MAIN_SITE_INDUCTION_ID = 69;
  private readonly OTHER_ENERGIA_COURSE_IDS = [35,39,40,41,43,44,45,46,47,48,49,50,51,52,53,63,64,123,127,392,507,514];

  constructor(
    private trainingService: TrainingService,
  ) {
  }

  public isEnergiaMainInduction(courseId: number) {
    return courseId === this.ENERGIA_MAIN_SITE_INDUCTION_ID;
  }

  public passAllConnectedEnergiaInductions(workerId): Observable<any> {
    let courseIds = this.OTHER_ENERGIA_COURSE_IDS;

    // forkJoin will run each course in parallel
    return forkJoin(courseIds.map((id) => this.createCourseRequests(id, workerId)));
  }

  private createCourseRequests(courseId, workerId): Observable<any> {
    // Create the worker link to the course
    return this.trainingService.createNewTrainingCourseForWorker({ courseId: courseId, workerUserId: workerId }).pipe(
      concatMap((courseResponse) => {
        let course = new TrainingCourseForUser().deserialize(courseResponse)
        let wucId = course.workerUserCourseId;

        // then for each section in order, complete the section
        // (reduce starts with null, then each one is piped onto the previous)
        let sectionRequests = course.course.sections.reduce((prev, section) => {
          if (section.sectionType === TrainingSectionType.Question) {
            return prev.pipe(concatMap(() => this.bypassQuizAndCatchError(section, wucId, course)));
          } else {
            return prev.pipe(concatMap(() => this.completeNonQuizSectionAndCatchError(section, wucId, course)));
          }
        }, of(null));
        return sectionRequests;
      }),
      catchError((error) => {
        console.log(`Error creating training course link ${courseId}: ${error.message}`);

        return of({ error: error, courseId: courseId });
      })
    );
  }

  private completeNonQuizSectionAndCatchError(section: TrainingCourseSection, wucId: number, course: TrainingCourseForUser) {
    return this.completeNonQuizSection(section, wucId).pipe(
      catchError((error) => {
        console.log(`Error auto-completeing section ${section.sectionName} of ${course.course.trainingName}: ${error.message}`);
        return of({ error: error, sectionId: section.courseSectionId });
      })
    );
  }

  public completeNonQuizSection(sectionData: TrainingCourseSection, workerUserCourseId: number): Observable<any> {
    const body = {
      workerUserCourseId: workerUserCourseId,
      courseSectionId: sectionData.courseSectionId,
    };

    return this.trainingService.submitSectionComplete(body);
  }

  private bypassQuizAndCatchError(section: TrainingCourseSection, wucId: number, course: TrainingCourseForUser) {
    return this.bypassQuiz(section, wucId).pipe(
      catchError((error) => {
        console.log(`Error auto-completeing section ${section.sectionName} of ${course.course.trainingName}: ${error.message}`);
        return of({ error: error, sectionId: section.courseSectionId });
      })
    );
  }

  private bypassQuiz(sectionData: TrainingCourseSection, workerUserCourseId): Observable<TrainingCourseQuestionSectionResponse> {
    const questionAndAnswers = sectionData.questions;

    const questionAndAnswerIds = questionAndAnswers.map((question) => {
      const correctAnswer = question.answers.find((answer) => answer.correct);
      return {
        questionId: question.question.id,
        answerId: correctAnswer.id,
      };
    });

    const body = {
      workerUserCourseId: workerUserCourseId,
      courseSectionId: sectionData.courseSectionId,
      questionAnswers: questionAndAnswerIds,
    };

    return this.trainingService.submitQuizAnswers(body);
  }
}
